import { memo, useState } from "react"
import { useTranslation } from "react-i18next"
import { formatDistance, format, isValid } from "date-fns"
import {
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  MessageSquare,
  MessageSquareWarning,
} from "lucide-react"
import { cva } from "class-variance-authority"

import {
  Button,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { TooltipContentPlus } from "@f2e/ui/components/base-plus"

import { ConversationFeedback } from "@/types"

const FEEDBACK_CONTENT_TRUNCATE_LENGTH = 100

type FeedbackCategory = "helpful" | "improve"

// CVA variants for FeedbackItem styling
const feedbackItemVariants = cva(
  "pl-3 pr-3 py-2.5 border-l-[3px] rounded-md bg-slate-50/80 dark:bg-slate-800/30",
  {
    variants: {
      category: {
        helpful: "border-l-emerald-500 dark:border-l-emerald-600",
        improve: "border-l-rose-500 dark:border-l-rose-500",
      },
    },
    defaultVariants: {
      category: "improve",
    },
  }
)

const feedbackIconVariants = cva("w-3.5 h-3.5", {
  variants: {
    category: {
      helpful: "text-emerald-600 dark:text-emerald-500",
      improve: "text-rose-600 dark:text-rose-500",
    },
  },
})

const feedbackLabelVariants = cva("text-xs font-medium", {
  variants: {
    category: {
      helpful: "text-emerald-700 dark:text-emerald-500",
      improve: "text-rose-700 dark:text-rose-500",
    },
  },
})

/**
 * Determines the display category for a feedback item.
 * - tag=system + type=sys_helpful → helpful
 * - tag=system + type=sys_improve → improve
 * - tag=custom → improve (all custom feedbacks are treated as improve)
 */
const getFeedbackCategory = (
  feedback: ConversationFeedback
): FeedbackCategory => {
  if (feedback.tag === "system" && feedback.type === "sys_helpful") {
    return "helpful"
  }
  return "improve"
}

/**
 * Gets the display label for a feedback type.
 * - system feedbacks show their type (Helpful / Improve)
 * - custom feedbacks show "Improve" (normalized)
 */
const getFeedbackTypeLabel = (
  feedback: ConversationFeedback,
  t: (key: string) => string
): string => {
  if (feedback.tag === "system") {
    return feedback.type === "sys_helpful"
      ? t("history.conversationFooter.feedback.helpful")
      : t("history.conversationFooter.feedback.improve")
  }
  // Custom feedbacks are normalized to "Improve"
  return t("history.conversationFooter.feedback.improve")
}

interface FeedbackItemProps {
  feedback: ConversationFeedback
}

/**
 * A single feedback item with a left border bar design.
 * The border color indicates the feedback type:
 * - Green for helpful feedback
 * - Amber/Orange for improvement feedback
 */
const FeedbackItem = memo(({ feedback }: FeedbackItemProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const category = getFeedbackCategory(feedback)

  const content = feedback.content || ""
  const shouldTruncate = content.length > FEEDBACK_CONTENT_TRUNCATE_LENGTH
  const displayContent =
    shouldTruncate && !isExpanded
      ? content.slice(0, FEEDBACK_CONTENT_TRUNCATE_LENGTH) + "..."
      : content

  const feedbackDate = new Date(feedback.createdAt)
  const hasValidDate = isValid(feedbackDate)
  const relativeTime = hasValidDate
    ? formatDistance(feedbackDate, new Date(), { addSuffix: true })
    : feedback.createdAt || "-"
  const absoluteTime = hasValidDate
    ? format(feedbackDate, "yyyy-MM-dd HH:mm:ss")
    : feedback.createdAt || "-"

  return (
    <div className={feedbackItemVariants({ category })}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          {category === "helpful" ? (
            <ThumbsUp className={feedbackIconVariants({ category })} />
          ) : (
            <MessageSquareWarning
              className={feedbackIconVariants({ category })}
            />
          )}
          <span className={feedbackLabelVariants({ category })}>
            {getFeedbackTypeLabel(feedback, t)}
          </span>
          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
            {feedback.identity.externalUserId?.trim() ||
              feedback.identity.userEmail?.trim() ||
              (feedback.identity.userId
                ? `User #${feedback.identity.userId}`
                : "Unknown")}
          </span>
        </div>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[11px] text-muted-foreground cursor-default">
                {relativeTime}
              </span>
            </TooltipTrigger>
            <TooltipContentPlus side="top">{absoluteTime}</TooltipContentPlus>
          </Tooltip>
        </TooltipProvider>
      </div>
      {content && (
        <div className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          <div className="whitespace-pre-wrap break-words py-2">
            {displayContent}
          </div>
          {shouldTruncate && (
            <Button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              variant="ghost"
              size="sm"
              className="mt-1 h-auto px-0 py-0 text-slate-700 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100 font-semibold hover:bg-transparent dark:hover:bg-transparent"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  {t("history.conversationFooter.feedback.showLess")}
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  {t("history.conversationFooter.feedback.showMore")}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  )
})

FeedbackItem.displayName = "FeedbackItem"

interface FeedbackSectionProps {
  feedbacks?: ConversationFeedback[]
}

/**
 * Displays a list of feedback items with a minimal, left-border-bar design.
 * This design is less prominent than full cards while still clearly indicating
 * feedback presence and type.
 */
const FeedbackSection = memo(({ feedbacks }: FeedbackSectionProps) => {
  const { t } = useTranslation()

  if (!feedbacks || feedbacks.length === 0) {
    return null
  }

  return (
    <div className="w-full mt-6 rounded-lg border-0 border-slate-200 bg-white text-slate-950 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50">
      <div className="mb-2">
        <MessageSquare className="inline w-4 h-4" />{" "}
        {t("history.conversationFooter.feedback.title")} ({feedbacks.length})
      </div>
      <div className="space-y-2">
        {feedbacks.map((feedback) => (
          <FeedbackItem key={feedback.id} feedback={feedback} />
        ))}
      </div>
    </div>
  )
})

FeedbackSection.displayName = "FeedbackSection"

export { FeedbackSection, FeedbackItem }
export type { FeedbackSectionProps, FeedbackItemProps }
export default FeedbackSection
